import Decimal from 'decimal.js';
import isNil from 'lodash/isNil';
import { getFormattedNumber } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { AxisTitleProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis-title';
import { GridLineProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/grid-line';
import { GridlinesType } from '../../../constant/chart';
import { ChartTextProperty } from '../chart-shape-property';
import { STAxPos } from '../../../Interface/chart-v3/sdk-model.interface';
import { ChartModelFactory } from '../../../chart-models/chart-presets/chart-model-factory';
import { type IAxisProperty, type AxisType, type IValueAxisScale, AxisName } from './axis.interface';
import type {
    IValuesRange,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import type {
    IAxisTitleProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis-title.interface';
import type { IBorderProperty } from '../../border/border.interface';
import type { ChartContext } from '@src/slide/domain/ui-model/element-modeles/chart-models/context';
import type {
    IChartAxis,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { IFillProperty } from '../../fill/fill.interface';
import type { IGridLineProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/grid-line.interface';
import type { NullableDeep } from '../chart-shape-property.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';

export class AxisProperty extends ChartTextProperty<IChartAxis> implements IAxisProperty {
    public type: AxisType;
    public index: number;
    public name: AxisName;
    public title: IAxisTitleProperty;
    public majorGridlines: IGridLineProperty;
    public minorGridlines: IGridLineProperty;
    public values: number[];
    public scale?: IValueAxisScale;
    public ticks?: number[];
    public isDelete: boolean;
    public autoLabeled: boolean;

    public constructor(protected uiData: IChartAxis, protected context: ChartContext) {
        super(uiData, context);
    }

    public init() {
        this.index = this.uiData.index || 0;
        this.isDelete = !!this.uiData.isDelete;
        this.autoLabeled = !!this.uiData.autoLabeled;
        this.type = this.uiData.type;

        const axisTitleData = this.uiData.title;
        if (axisTitleData) {
            this.title = new AxisTitleProperty(axisTitleData, this.context).init(this);
        }

        const majorGrindLinesData = this.uiData.majorGridlines;
        if (majorGrindLinesData) {
            this.majorGridlines = new GridLineProperty(
                majorGrindLinesData,
                this.context,
                GridlinesType.MAJOR_GRID,
            ).init(this);
        }
        const minorGrindLinesData = this.uiData.minorGridlines;
        if (minorGrindLinesData) {
            this.minorGridlines = new GridLineProperty(
                minorGrindLinesData,
                this.context,
                GridlinesType.MINOR_GRID,
            ).init(this);
        }

        return this;
    }

    public setAxisName(name: AxisName) {
        this.name = name;
    }

    public setValues(values: number[], range?: IValuesRange) {
        this.values = values;

        // setScale是setValues附带的副作用，scale跟随values的更新而更新
        this.setScale(range);

        this.setTicks();
    }

    // 需要外面先设置values，再初始化scale。因为组合图可能涉及多个子表数据，所以需要外界集中处理后传入
    public setScale(range?: IValuesRange) {
        this.scale = this.getScale(this.uiData, range);
    }

    public updateProps(props?: Partial<IChartAxis>, removeProps?: Partial<NullableDeep<IChartAxis>>): void {
        const relativeSubCharts = this.getRelativeSubCharts();

        transaction(() => {
            relativeSubCharts?.map((subChart) => {
                const { axes } = subChart;
                const axisIndex = axes!.findIndex(axis => axis.axId === this.uiData.axId);

                const updateData: Partial<ISubChartModel> = {};
                if (props) {
                    const updateAxes = new Array(axisIndex + 1).fill(undefined);
                    updateAxes[axisIndex] = {
                        ...props,
                        // 需要带上type，不然ui2sdk的不知道类型，时候无法拼出egAxis
                        type: this.type,
                    };
                    updateData.axes = updateAxes;
                }

                const removeData: Partial<ISubChartModel> = {};
                if (removeProps) {
                    const removeAxes = new Array(axisIndex + 1).fill(undefined);
                    removeAxes[axisIndex] = {
                        ...removeProps,
                        // 需要带上type，不然ui2sdk的不知道类型，时候无法拼出egAxis
                        type: this.type,
                    };
                    removeData.axes = removeAxes;
                }

                subChart.updateProps(updateData, removeData);
            });
        });
    }

    // @ts-ignore
    public getBorder(): IBorderProperty {}
    // @ts-ignore
    public getBackgroundFill(): IFillProperty {}
    public showAxis(): void {
        if (this.name === AxisName.SECONDARY_HORIZONTAL) {
            this.updateProps({
                isDelete: false,
                position: STAxPos.t,
            });
        } else {
            this.updateProps({
                isDelete: false,
            });
        }
    }

    public hideAxis(): void {
        this.updateProps({
            isDelete: true,
        });
    }

    public createAxisTitle(): void {
        this.updateProps({
            title: ChartModelFactory.createAxisTitle(),
        });
    }

    public removeAxisTitle(): void {
        this.updateProps(undefined, {
            title: null,
        });
    }

    public createGridlines(gridlinesTypes: GridlinesType[]): void {
        const props = {};
        gridlinesTypes.forEach((gridlinesType) => {
            props[gridlinesType] = ChartModelFactory.createGridlines(gridlinesType);
        });
        this.updateProps(props);
    }

    public removeGridlines(gridlinesTypes: GridlinesType[]): void {
        const props = {};
        gridlinesTypes.forEach((gridlinesType) => {
            props[gridlinesType] = null;
        });
        this.updateProps(undefined, props);
    }

    private getRelativeSubCharts() {
        const { axId } = this.uiData;
        if (!axId) return;

        const subCharts = this.context.getPropertyModule('subCharts');
        return subCharts?.filter(subChart => subChart.axisIds?.includes(axId));
    }

    /* range是业务可以指定的最大最小值范围。用于拓展坐标轴的范围，比如气泡图需要通过气泡的大小进行range计算 */
    private getScale(axis: IChartAxis, range?: [number, number]): IValueAxisScale {
        const valueMin = range ? range[0] : Math.min(...this.values);
        const valueMax = range ? range[1] : Math.max(...this.values);
        const { max: scaleMax, min: scaleMin, logBase } = axis.scaling || {};
        const { majorUnit: scaleMajorUnit, minorUnit: scaleMinorUnit } = axis;

        return this.calcMinMaxAndUnit(
            valueMax, valueMin,
            scaleMax, scaleMin,
            scaleMajorUnit, scaleMinorUnit,
            logBase,
        );
    }

    private setTicks() {
        if (this.scale) {
            const { logBase } = (this as IChartAxis).scaling || {};
            const { max, majorUnit, min } = this.scale;

            this.ticks = this.getAxisTicks(max, min, majorUnit, logBase);
        }
    }

    // eslint-disable-next-line complexity
    private calcMinMaxAndUnit(maxValue, minValue, settingMax, settingMin, majorUnit, minorUnit, logBase?) {
        const settingFallback = {
            max: settingMax,
            min: settingMin,
            majorUnit,
            minorUnit,
        };
        if (isNaN(Number(maxValue)) || isNaN(Number(minValue))
            || !Number.isFinite(maxValue) || !Number.isFinite(minValue)) {
            return settingFallback;
        }
        maxValue = this.formatNumber(maxValue);
        minValue = this.formatNumber(minValue);
        settingMax = this.formatNumber(settingMax);
        settingMin = this.formatNumber(settingMin);
        majorUnit = this.formatNumber(majorUnit);
        minorUnit = this.formatNumber(minorUnit);

        if (logBase) {
            maxValue = maxValue ?? 1;
            minValue = minValue ?? 1;
            let max = settingMax ?? Math.max(maxValue, 1);
            let min = settingMin ?? Math.min(minValue, 1);
            // 如果设置了单位和对数， 则以设置的单位为对数底数
            majorUnit = majorUnit || logBase;
            max =  majorUnit ** Math.ceil(this.mathLogBase(max, majorUnit));
            min =  majorUnit ** Math.floor(this.mathLogBase(min, majorUnit)) || 1;
            return {
                max, min, majorUnit, minorUnit: minorUnit ?? majorUnit,
            };
        }
        // 计算坐标轴范围, 确定坐标轴起始值
        maxValue = maxValue ?? 0;
        minValue = minValue ?? 0;
        let max = settingMax ?? Math.max(maxValue, 0);
        let min = settingMin ?? Math.min(minValue, 0);
        if (!(!isNil(settingMax) && !isNil(settingMin))) {
            const valueRange = (settingMax ?? maxValue) - (settingMin ?? minValue);
            const concentrationThreshold = 1 / 6;
            if (valueRange && valueRange / Math.max(Math.abs(maxValue), Math.abs(minValue)) <= concentrationThreshold) {
                if (max > 0 && isNil(settingMin)) {
                    // 坐标轴起始值与坐标轴范围 需大于数据范围* concentrationThreshold
                    // minValue - min >= concentrationThreshold * (maxValue - min )
                    min = (maxValue - (minValue / concentrationThreshold)) / (1 - (1 / concentrationThreshold));
                } else if (min < 0 && isNil(settingMax)) {
                    max = (minValue - (maxValue / concentrationThreshold)) / (1 - (1 / concentrationThreshold));
                }
            }
        }
        let unit = majorUnit;
        const range = Decimal.sub(max, min);
        if (!range || !Number(range)) return settingFallback;
        const with5Parts = range.div(5);
        const with10Parts = range.div(10);
        const unitBase = [1, 2, 5]; // 刻度基数
        let digit = Number(with10Parts.abs().log(10)
            .floor());
        // 计算刻度
        const startTime = Date.now();
        while (!unit && digit <= Number(with5Parts)) {
            // 兜底， 避免出现意外场景死循环
            if (Date.now() - startTime > 500) {
                this.reportAxisCalculateError({
                    maxValue,
                    minValue,
                    settingMax,
                    settingMin,
                    majorUnit,
                    minorUnit,
                    logBase,
                });
                return settingFallback;
            }
            // eslint-disable-next-line no-restricted-syntax
            for (const base of unitBase) {
                const temp = base * (10 ** digit);
                if (temp * 4 <= Number(range) && temp * 10 > Number(range)) {
                    unit = temp;
                    break;
                }
            }
            digit += 1;
        }

        if (!unit) return settingFallback;
        // 计算最大刻度、最小刻度
        let scaleMax;
        let scaleMin;
        if (settingMax !== undefined && settingMin !== undefined) {
            scaleMax = settingMax;
            scaleMin = settingMin;
        } else if (settingMax !== undefined) {
            scaleMax = settingMax;
            const offset = Decimal.mod(scaleMax, unit).isZero() ? 0 : (Decimal.mod(scaleMax, unit).minus(unit));
            scaleMin = (Math.floor(min / unit) * unit) + Number(offset);
        } else if (settingMin !== undefined) {
            scaleMin = settingMin;
            scaleMax = max ? (((Math.floor(Number(range) / unit) + 1) * unit) + min) : 0;
        } else {
            scaleMax = max ? (Math.ceil(max / unit) + (Decimal.mod(max, unit).isZero() ? 1 : 0)) * unit : 0;
            scaleMin = min ? (Math.floor(min / unit) - (Decimal.mod(min, unit).isZero() ? 1 : 0)) * unit : 0;
        }
        // data最值临近刻度边界时, 扩展刻度边界
        //  > unit * 0.0001 浮点是否相等
        if ((scaleMax - max) > unit * 0.0001 && Math.abs(scaleMax - max) < unit * (1 - 0.5715)) {
            scaleMax += unit;
        }
        if (Math.abs(scaleMin - min) > unit * 0.0001 && Math.abs(scaleMin - min) < unit * (1 - 0.5715)) {
            scaleMin -= unit;
        }
        minorUnit = minorUnit ?? unit / 5;
        return {
            max: this.formatNumber(scaleMax),
            min: this.formatNumber(scaleMin),
            majorUnit: this.formatNumber(unit),
            minorUnit: this.formatNumber(minorUnit),
        };
    }


    private getAxisTicks(max: number, min: number, unit: number, logBase?: number): number[] | undefined {
        const ticks: any[] = [];
        if (isNaN(Number(max)) || isNaN(Number(min)) || isNaN(Number(unit))) {
            return;
        }
        const dmax = new Decimal(max);
        const dmin = new Decimal(min);
        let temp = dmin;
        const startTime = Date.now();
        while (temp.lessThanOrEqualTo(dmax)) {
            // 兜底， 避免出现意外场景死循环
            if (Date.now() - startTime > 500) {
                this.reportAxisTicksCalculateError({
                    max,
                    min,
                    unit,
                    logBase,
                });
                return;
            }
            ticks.push(temp.valueOf());
            if (logBase) {
                temp = temp.mul(unit);
            } else {
                temp = temp.add(unit);
            }
        }
        return ticks;
    }

    private mathLogBase(val, base) {
        return Math.log(val) / Math.log(base);
    }

    private formatNumber(n) {
        if (isNaN(n) || isNil(n)) return n;
        return getFormattedNumber(n);
    }

    private reportAxisCalculateError(options) {
        const ver6Param = JSON.stringify(options);

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.CHART,
            action: TDWActionType.AXIS_CALCULATE_ERROR,
            ver6: ver6Param,
        });
    }

    private reportAxisTicksCalculateError(options) {
        const ver6Param = JSON.stringify(options);

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.CHART,
            action: TDWActionType.AXIS_TICKS_CALCULATE_ERROR,
            ver6: ver6Param,
        });
    }

    private get model() {
        return this.context.getChartModel();
    }

    private getAxesName(type: AxisType): string {
        return `${type}Axes`;
    }
}
